import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const VUEX_PROPERTIES = ['state', 'getters', 'actions', 'mutations']

let store = {};

(function updateModules () {
  // If store is an exported method = classic mode (deprecated)

  // Enforce store modules
  store.modules = store.modules || {}

  resolveStoreModules(require('../store/app.ts'), 'app.ts')
  resolveStoreModules(require('../store/form.ts'), 'form.ts')
  resolveStoreModules(require('../store/types.ts'), 'types.ts')
  resolveStoreModules(require('../store/affiliate/affiliate.ts'), 'affiliate/affiliate.ts')
  resolveStoreModules(require('../store/agent-model/agent-model.ts'), 'agent-model/agent-model.ts')
  resolveStoreModules(require('../store/agent/agent.ts'), 'agent/agent.ts')
  resolveStoreModules(require('../store/campaign/campaign.ts'), 'campaign/campaign.ts')
  resolveStoreModules(require('../store/consent-message/consent-message.ts'), 'consent-message/consent-message.ts')
  resolveStoreModules(require('../store/customer-consent/customer-consent.ts'), 'customer-consent/customer-consent.ts')
  resolveStoreModules(require('../store/customerJourney/customerJourney.ts'), 'customerJourney/customerJourney.ts')
  resolveStoreModules(require('../store/customerJourney/customerJourneyStat.ts'), 'customerJourney/customerJourneyStat.ts')
  resolveStoreModules(require('../store/data-adapter/data-adapter.ts'), 'data-adapter/data-adapter.ts')
  resolveStoreModules(require('../store/data-plugin/data-plugin.ts'), 'data-plugin/data-plugin.ts')
  resolveStoreModules(require('../store/database/database.ts'), 'database/database.ts')
  resolveStoreModules(require('../store/external-api/external-api.ts'), 'external-api/external-api.ts')
  resolveStoreModules(require('../store/landingpage/landingpage.ts'), 'landingpage/landingpage.ts')
  resolveStoreModules(require('../store/landingpage/theme.ts'), 'landingpage/theme.ts')
  resolveStoreModules(require('../store/line/line.ts'), 'line/line.ts')
  resolveStoreModules(require('../store/masterdata/masterdata.ts'), 'masterdata/masterdata.ts')
  resolveStoreModules(require('../store/script-gallery/script-gallery.ts'), 'script-gallery/script-gallery.ts')
  resolveStoreModules(require('../store/segment/segment.ts'), 'segment/segment.ts')
  resolveStoreModules(require('../store/short-url/short-url.ts'), 'short-url/short-url.ts')
  resolveStoreModules(require('../store/template/attention.ts'), 'template/attention.ts')
  resolveStoreModules(require('../store/template/email.ts'), 'template/email.ts')
  resolveStoreModules(require('../store/user/user.ts'), 'user/user.ts')
  resolveStoreModules(require('../store/visualization/dashboard.ts'), 'visualization/dashboard.ts')
  resolveStoreModules(require('../store/visualization/funnel.ts'), 'visualization/funnel.ts')
  resolveStoreModules(require('../store/line/models/oa_info.ts'), 'line/models/oa_info.ts')
  resolveStoreModules(require('../store/line/models/richmenu.ts'), 'line/models/richmenu.ts')

  // If the environment supports hot reloading...
})()

// createStore
export const createStore = store instanceof Function ? store : () => {
  return new Vuex.Store(Object.assign({
    strict: (process.env.NODE_ENV !== 'production')
  }, store))
}

function normalizeRoot (moduleData, filePath) {
  moduleData = moduleData.default || moduleData

  if (moduleData.commit) {
    throw new Error(`[nuxt] ${filePath} should export a method that returns a Vuex instance.`)
  }

  if (typeof moduleData !== 'function') {
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData)
  }
  return normalizeModule(moduleData, filePath)
}

function normalizeModule (moduleData, filePath) {
  if (moduleData.state && typeof moduleData.state !== 'function') {
    console.warn(`'state' should be a method that returns an object in ${filePath}`)

    const state = Object.assign({}, moduleData.state)
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData, { state: () => state })
  }
  return moduleData
}

function resolveStoreModules (moduleData, filename) {
  moduleData = moduleData.default || moduleData
  // Remove store src + extension (./foo/index.js -> foo/index)
  const namespace = filename.replace(/\.(js|mjs|ts)$/, '')
  const namespaces = namespace.split('/')
  let moduleName = namespaces[namespaces.length - 1]
  const filePath = `store/${filename}`

  moduleData = moduleName === 'state'
    ? normalizeState(moduleData, filePath)
    : normalizeModule(moduleData, filePath)

  // If src is a known Vuex property
  if (VUEX_PROPERTIES.includes(moduleName)) {
    const property = moduleName
    const propertyStoreModule = getStoreModule(store, namespaces, { isProperty: true })

    // Replace state since it's a function
    mergeProperty(propertyStoreModule, moduleData, property)
    return
  }

  // If file is foo/index.js, it should be saved as foo
  const isIndexModule = (moduleName === 'index')
  if (isIndexModule) {
    namespaces.pop()
    moduleName = namespaces[namespaces.length - 1]
  }

  const storeModule = getStoreModule(store, namespaces)

  for (const property of VUEX_PROPERTIES) {
    mergeProperty(storeModule, moduleData[property], property)
  }

  if (moduleData.namespaced === false) {
    delete storeModule.namespaced
  }
}

function normalizeState (moduleData, filePath) {
  if (typeof moduleData !== 'function') {
    console.warn(`${filePath} should export a method that returns an object`)
    const state = Object.assign({}, moduleData)
    return () => state
  }
  return normalizeModule(moduleData, filePath)
}

function getStoreModule (storeModule, namespaces, { isProperty = false } = {}) {
  // If ./mutations.js
  if (!namespaces.length || (isProperty && namespaces.length === 1)) {
    return storeModule
  }

  const namespace = namespaces.shift()

  storeModule.modules[namespace] = storeModule.modules[namespace] || {}
  storeModule.modules[namespace].namespaced = true
  storeModule.modules[namespace].modules = storeModule.modules[namespace].modules || {}

  return getStoreModule(storeModule.modules[namespace], namespaces, { isProperty })
}

function mergeProperty (storeModule, moduleData, property) {
  if (!moduleData) {
    return
  }

  if (property === 'state') {
    storeModule.state = moduleData || storeModule.state
  } else {
    storeModule[property] = Object.assign({}, storeModule[property], moduleData)
  }
}
